import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import set from 'lodash/set';
import pick from 'lodash/pick';
import base64url from 'base64url';
import { DOCTYPE_PRINT_TEMPLATE, DOCTYPE_SALE_TRANSACTION } from '@krispsystems/constants';

import * as c from '../../constants';

import * as apiSaleTransaction from '../../services/sale_transaction/api_sale_transaction';
import * as apiCommon from '../../services/apiCommon';
import * as apiCustomer from '../../services/customer/api_customer';

import { getParticularPreferenceSettings } from '../../common/selectors/preference';
import { getCurrentUserId, isUserAdmin } from '../../common/selectors/selector_common';

import { buildTransactionTemplate } from '../export_transaction/saga_export_transaction';
import Handlebars from '../../utils/handlebar';

export default function* (feathersApp) {
  try {
    yield all([
      fork(ereceiptSendLinkSaga, feathersApp),
      fork(ereceiptSaleSaga, feathersApp),
    ]);
  } catch (err) {
    throw err;
  }
}

function* ereceiptSaveContact(feathersApp, params) {
  const { transaction, type, value } = params;

  const data = pick(
    transaction,
    '_id',
    'customer_id',
    'doc_type',
    'id',
    'sales_rep_id',
    'tid',
    'ereceipt',
  );

  if (type === 'email') {
    set(data, 'ereceipt.email', value);
  }

  if (type === 'mobile') {
    set(data, 'ereceipt.mobile_number', value);
  }

  const newTransaction = yield call(apiSaleTransaction.saveSaleTransaction, feathersApp, {
    data
  });

  return newTransaction;
}

function* ereceiptSendLinkSaga(feathersApp) {
  yield takeLatest(c.ERECEIPT_LINK_SEND, ereceiptSendLink, feathersApp);
}

function* ereceiptSendLink(feathersApp, action) {
  try {
    const { payload } = action;
    const {
      from,
      isResave,
      transactionId,
      type,
      value,
    } = payload;

    yield put({ type: c.ERECEIPT_LINK_SENDING, payload: { value, type, from } });

    const { app } = yield select();
    const { api_host, env_id, tenant_id } = get(app, 'payload', {});

    const hostPrefix = api_host.split('.').shift();
    const hash = base64url(`${transactionId}.${tenant_id}.${hostPrefix}`);
    const link = `https://${env_id}-ereceipt.krispsystems.net/${hash}`;

    let transaction = yield call(
      apiCommon.getDoc,
      feathersApp,
      {
        id: transactionId,
        docType: DOCTYPE_SALE_TRANSACTION,
      },
    );

    if (isResave) {
      const params = {
        transaction,
        type,
        value,
      };

      transaction = yield call(
        ereceiptSaveContact,
        feathersApp,
        params,
      );
    }

    const templateData = yield call(buildTransactionTemplate, feathersApp, transaction);
    templateData.meta.link = link;

    const locationId = transaction.location_id;
    const subsidiaryId = transaction.subsidiary_id;
    const entityId = transaction.customer_id;

    switch (type) {
      case 'email': {
        const { value: emailEreceiptTemplateId } = yield select(
          getParticularPreferenceSettings,
          'email_body_template_sale_ereceipt_link',
          ['value'],
          locationId,
          subsidiaryId,
        );

        const emailEreceiptTemplate = yield call(apiCommon.getDoc, feathersApp, {
          id: emailEreceiptTemplateId,
          docType: DOCTYPE_PRINT_TEMPLATE,
        });

        const { template_text, template_text_subject } = emailEreceiptTemplate;

        const textCompiler = Handlebars.compile(template_text);
        const subjectCompiler = Handlebars.compile(template_text_subject);

        const text = textCompiler(templateData);
        const subject = subjectCompiler(templateData);

        const opts = {
          text,
          subject,
          to: value,
        };

        yield call(sendLinkEmail, opts, feathersApp);
        break;
      }

      case 'mobile': {
        const { value: smsEreceiptTemplateId } = yield select(
          getParticularPreferenceSettings,
          'sms_template_sale_ereceipt_link',
          ['value'],
          locationId,
          subsidiaryId,
        );

        const smsEreceiptTemplate = yield call(apiCommon.getDoc, feathersApp, {
          id: smsEreceiptTemplateId,
          docType: DOCTYPE_PRINT_TEMPLATE,
        });

        const { template_text } = smsEreceiptTemplate;

        const textCompiler = Handlebars.compile(template_text);
        const text = textCompiler(templateData);
        const opts = {
          entityId,
          locationId,
          subsidiaryId,
          content: text,
          number: value,
        };

        yield call(sendLinkSms, opts, feathersApp);
        break;
      }

      default:
        break;
    }

    yield put({ type: c.ERECEIPT_LINK_SENDING_SUCCESS });
    yield put({ type: c.ERECEIPT_MODAL_HIDE });
  } catch (err) {
    yield put({ type: c.ERECEIPT_LINK_SENDING_FAILED });
  }
}

function* ereceiptSaleSaga(feathersApp) {
  yield takeLatest(c.ERECEIPT_SALE, ereceiptSale, feathersApp);
}

function* ereceiptSale(feathersApp, action) {
  try {
    yield put({ type: c.ERECEIPT_MODAL_SHOW });
    yield put({ type: c.ERECEIPT_CONTACTS_FETCHING });

    const { path } = yield select();

    const transactionId = action.payload;
    const transaction = yield call(
      apiSaleTransaction.fetchSaleTransactionById,
      feathersApp,
      { id: transactionId, path }
    );

    const contacts = yield call(getContacts, transaction, feathersApp);

    yield put({
      type: c.ERECEIPT_CONTACTS_FETCHING_DONE,
      payload: {
        contacts,
        transactionId,
      },
    });
  } catch (err) {
    yield put({ type: c.ERECEIPT_MODAL_RESET });
  }
}

async function sendLinkSms(opts, feathersApp) {
  const { content, number, entityId, locationId, subsidiaryId } = opts;
  const smsOpts = {
    content,
    destination_number: number,
  };

  if (entityId) {
    await apiCommon.sms(
      feathersApp,
      smsOpts,
      {
        id: entityId,
        location: locationId,
        subsidiary: subsidiaryId,
      },
    );
  } else {
    const service = feathersApp.service('/sms');
    await service.create({
      ...smsOpts,
      location_id: locationId,
      subsidiary_id: subsidiaryId,
    });
  }
}

async function sendLinkEmail(opts, feathersApp) {
  const { to, text, subject } = opts;

  const emailParams = {
    text,
    subject,
    to,
  };

  await apiCommon.email(feathersApp, emailParams);
}

function* getContacts(transaction, feathersApp) {
  const customerId = get(transaction, 'customer_id');
  const txMobile = get(transaction, 'ereceipt.mobile_number', '');

  const contactsSale = {
    mobile: txMobile,
  };

  const mobiles = [];
  const emails = [];

  if (customerId) {
    const { path } = yield select();
    const customer = yield call(apiCustomer.fetchCustomerForEdit, feathersApp, {
      path,
      id: customerId,
    });

    const isAdmin = yield select(isUserAdmin);
    const userId = yield select(getCurrentUserId);
    const isOwner = userId === customerId;

    if (!Boolean(customer.private_contact_details) || isAdmin || isOwner) {
      const { contact_numbers } = customer || {};

      contact_numbers.forEach(({ value }) => {
        mobiles.push({ value, type: 'mobile' });
      });
    }
  }

  const contactsCustomer = [ ...mobiles, ...emails ];
  const contacts = {
    customer: contactsCustomer,
    sale: contactsSale,
  };

  return contacts;
}
